import { Check, Sparkles } from "lucide-react";

const INKLUSIVE = [
  "Unkrautentfernung auf Pflasterflächen, Wegen und Parkplätzen",
  "Laubentfernung",
  "Rasenmähen vorhandener Grünflächen",
  "Schneiden der Rasenkanten",
];

export default function AktionsAngebot({ onCta }) {
  return (
    <section
      id="aktion"
      className="relative bg-[#FAFAF5] py-20 sm:py-24 overflow-hidden"
      data-testid="aktion-section"
    >
      {/* subtle amber glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-[#F5A623]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#6B7F3E]/10 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Section eyebrow */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#F5A623]/40 px-4 py-1.5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" strokeWidth={2.5} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B87A00]">
              Neukunden-Aktion · Herbst 2026
            </span>
          </div>
        </div>

        {/* Card */}
        <div className="relative rounded-3xl bg-[#2C3E2D] text-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">

            {/* LEFT — portrait + slogan */}
            <div className="lg:col-span-2 relative">
              <img
                src="/marvin_laubsauger.jpg"
                alt="Marvin Blessing mit Schutzhelm und Laubsauger bei der Laubentfernung"
                width={1080}
                height={1350}
                loading="lazy"
                className="w-full h-80 sm:h-96 lg:h-full object-cover object-[50%_28%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A201A]/85 via-[#1A201A]/30 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#2C3E2D]/40" />
              <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C4CFB1] font-medium mb-1.5">
                  Ihr Ansprechpartner
                </div>
                <div className="font-display text-2xl">Marvin Blessing</div>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-white/90">
                  <span className="rounded-full bg-white/10 backdrop-blur px-2.5 py-1">
                    saubere Flächen
                  </span>
                  <span className="rounded-full bg-white/10 backdrop-blur px-2.5 py-1">
                    gepflegte Außenanlagen
                  </span>
                  <span className="rounded-full bg-white/10 backdrop-blur px-2.5 py-1">
                    faire Preise
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT — offer + CTA */}
            <div className="lg:col-span-3 p-8 sm:p-10 lg:p-12 flex flex-col justify-between gap-8">
              <div>
                <h2 className="font-display font-bold tracking-tight leading-[0.95] text-[#F5A623] text-5xl sm:text-6xl">
                  AKTIONS<br />ANGEBOT!
                </h2>

                <div className="mt-8">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C4CFB1] mb-3">
                    Im Angebot inbegriffen
                  </div>
                  <ul className="space-y-2.5">
                    {INKLUSIVE.map((line) => (
                      <li key={line} className="flex items-start gap-2.5 text-white/95">
                        <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#F5A623]/25 ring-1 ring-[#F5A623]/50">
                          <Check className="w-2.5 h-2.5 text-[#F5A623]" strokeWidth={3.5} />
                        </span>
                        <span className="text-[15px] leading-snug">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* price + CTA */}
              <div className="relative rounded-2xl bg-gradient-to-br from-[#3B5236] to-[#2C3E2D] ring-1 ring-white/10 p-6 sm:p-7 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-[#F5A623]/15 blur-2xl" />
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C4CFB1]">
                      Jetzt Aktionspreis sichern
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="font-display text-6xl sm:text-7xl font-bold text-[#F5A623] leading-none">
                        99€
                      </span>
                      <span className="text-[#F5A623]/80 text-2xl leading-none">*</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onCta}
                    data-testid="aktion-cta-btn"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#F5A623] hover:bg-[#E29614] text-[#1A201A] px-7 py-3.5 font-semibold text-sm shadow-lg shadow-[#F5A623]/30 transition-all hover:scale-[1.02] active:scale-100"
                  >
                    Jetzt Angebot sichern
                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                  </button>
                </div>
              </div>

              <p className="text-[11px] leading-relaxed text-white/55">
                *Angebot gilt einmalig für Neukunden im Zeitraum 01.09.2026 – 24.12.2026.
                Umfang der Leistung wird vom Dienstleister bestimmt.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
